import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const HiPot = ({ formData, onFormDataChange }) => {
    const { safetyTestObservations = [], nonConformance = '' } = formData;

    useEffect(() => {
        // Destructure formData to get initial values
        const { safetyTestObservations = [], nonConformance = '' } = formData;

        // Check if formData already has prefilled data
        const hasFormData =
            safetyTestObservations.length > 0 || nonConformance.trim() !== '';

        if (!hasFormData) {
            // Step 1: Fetch metadata from sessionStorage
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));

            let prefilledSafetyTestData = [];
            let prefilledObservations = [];
            let prefilledNonConformance = '';

            // Step 2: Check metadata for prefilled data
            if (
                metadata &&
                metadata.post &&
                metadata.post.HiPot_post
            ) {
                prefilledSafetyTestData = metadata.post.HiPot_post;
                prefilledObservations = prefilledSafetyTestData.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: '',
                    observation2: '',
                }));
            }

            const inspectionData = JSON.parse(
                sessionStorage.getItem('inspectionData')
            );

            // Step 3: Check inspectionData for observations and non-conformance data
            if (
                inspectionData &&
                inspectionData.PostLamination &&
                inspectionData.PostLamination.HiPot_post
            ) {
                const { HiPot, nonConformance: inspectionNonConformance } =
                    inspectionData.PostLamination.HiPot_post;

                prefilledSafetyTestData = HiPot;
                prefilledObservations = HiPot.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));

                prefilledNonConformance = inspectionNonConformance || '';
            }

            // Step 4: Update formData only if prefilled data exists
            if (
                prefilledSafetyTestData.length > 0 ||
                prefilledObservations.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    safetyTestObservations: prefilledObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);

    const handleObservationChange = (index, field, value) => {
        const updatedObservations = [...safetyTestObservations];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, safetyTestObservations: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const incompleteObservation = safetyTestObservations.some(
            (obs) => !obs.observation1.trim()
        );
        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }

        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

            const combinedData = safetyTestObservations.map((item, index) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: safetyTestObservations[index]?.observation1 || '',
                observation2: safetyTestObservations[index]?.observation2 || '',
            }));

            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PostLamination: {
                    HiPot_post: {
                        HiPot: combinedData,
                        nonConformance: nonConformance,
                    },
                },
            };

            await submitFormData(payload);
            alert('Observations submitted successfully!');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations.');
        }
    };

    const renderSafetyTestTable = () => (
        <div className="mt-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-700">Safety Test</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formData.safetyTestObservations?.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={item.observation1 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(index, 'observation1', e.target.value)
                                        }
                                        placeholder="Enter observation 1"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={item.observation2 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(index, 'observation2', e.target.value)
                                        }
                                        placeholder="Enter observation 2"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">HiPot Inspection</h3>
            {renderSafetyTestTable()}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={nonConformance || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border p-4 rounded-lg focus:outline-none"
                    rows={4}
                />
            </div>
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500"
            >
                Submit
            </button>
        </div>
    );
};

export default HiPot;